import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ChevronRight, Check, FileBadge, IdCard, Camera, PenTool, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PhotoCapture from "../components/PhotoCapture";
import SignaturePad from "../components/SignaturePad";
import LoadingButton from "../components/LoadingButton";

const STEPS = [
  { key: "akun", label: "Akun" },
  { key: "data", label: "Data Diri" },
  { key: "foto", label: "Foto" },
  { key: "ttd", label: "Tanda Tangan" },
  { key: "review", label: "Review" },
];

export default function RegisterPage() {
  const { register, config } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", password: "", confirm_password: "",
    ktp_no: "", phone: "", account_no: "",
    address: "", place_of_birth: "", date_of_birth: "", gender: "L", occupation: "",
    membership_type: "biasa",
    photo_selfie: null, photo_ktp: null, signature: null,
  });

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const validateStep = () => {
    setError("");
    if (step === 0) {
      if (!form.full_name || !form.email || !form.password) return "Lengkapi nama, email, dan password";
      if (form.password.length < 6) return "Password minimal 6 karakter";
      if (form.password !== form.confirm_password) return "Konfirmasi password tidak cocok";
    }
    if (step === 1) {
      if (!form.ktp_no || form.ktp_no.length < 10) return "No KTP minimal 10 digit";
      if (!form.phone) return "No HP wajib diisi";
    }
    if (step === 2) {
      if (!form.photo_selfie || !form.photo_ktp) return "Foto diri dan foto KTP wajib";
    }
    if (step === 3) {
      if (!form.signature) return "Tanda tangan wajib";
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setError("");
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => { setError(""); setStep((s) => Math.max(s - 1, 0)); };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    const payload = { ...form };
    delete payload.confirm_password;
    if (!payload.date_of_birth) delete payload.date_of_birth;
    const res = await register(payload);
    setSubmitting(false);
    if (!res.ok) { setError(res.error); toast.error(res.error); return; }
    toast.success("Pendaftaran berhasil. Menunggu persetujuan admin.");
    nav("/dashboard");
  };

  return (
    <div className="min-h-screen bg-mint-pattern">
      <header className="bg-brand text-white">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center gap-3">
          {config?.logo ? (
            <img src={config.logo} alt="logo" className="w-10 h-10 rounded-xl object-cover ring-1 ring-white/20" />
          ) : (
            <div className="w-10 h-10 rounded-xl bg-gold flex items-center justify-center font-heading font-bold text-brand">K</div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.22em] text-white/60">Pendaftaran</div>
            <div className="font-heading font-bold truncate">{config?.name || "Kopsebra"}</div>
          </div>
          <Link to="/" data-testid="register-back-to-login" className="rounded-full bg-white/10 hover:bg-white/20 transition px-3 py-2 text-xs flex items-center gap-1.5">
            <ArrowLeft size={14} /> Sudah punya akun? Masuk
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-6 pb-20">
        <h1 className="font-heading font-extrabold text-2xl sm:text-3xl text-brand-ink tracking-tight">Registrasi Anggota Koperasi</h1>
        <p className="text-brand-muted text-sm mt-1">Lengkapi data Anda. Pendaftaran akan diverifikasi oleh admin.</p>

        {/* Stepper */}
        <ol className="mt-5 mb-6 flex items-center gap-1 sm:gap-2 overflow-x-auto no-scrollbar" data-testid="register-stepper">
          {STEPS.map((s, i) => (
            <li key={s.key} className="flex items-center gap-1 sm:gap-2 shrink-0">
              <div className={
                "w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold " +
                (i < step ? "bg-emerald-500 text-white" : i === step ? "bg-brand text-white" : "bg-white text-brand-muted border border-brand/15")
              }>{i < step ? <Check size={14} /> : i + 1}</div>
              <span className={"text-xs sm:text-sm " + (i === step ? "text-brand-ink font-semibold" : "text-brand-muted")}>{s.label}</span>
              {i < STEPS.length - 1 && <ChevronRight size={14} className="text-brand-muted/50" />}
            </li>
          ))}
        </ol>

        <div className="bg-white rounded-3xl border border-brand/10 shadow-sm p-5 sm:p-7 animate-fade-up">
          {/* STEP 0 */}
          {step === 0 && (
            <div className="space-y-4" data-testid="register-step-akun">
              <SectionTitle icon={ShieldCheck} title="Buat Akun" subtitle="Email akan digunakan sebagai username login" />
              <Field label="Nama Lengkap" required>
                <input data-testid="reg-fullname" className="inp" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Email" required>
                  <input data-testid="reg-email" type="email" className="inp" value={form.email} onChange={(e) => set("email", e.target.value)} />
                </Field>
                <Field label="No HP" required>
                  <input data-testid="reg-phone" className="inp" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                </Field>
                <Field label="Password" required>
                  <input data-testid="reg-password" type="password" className="inp" value={form.password} onChange={(e) => set("password", e.target.value)} />
                </Field>
                <Field label="Konfirmasi Password" required>
                  <input data-testid="reg-password-confirm" type="password" className="inp" value={form.confirm_password} onChange={(e) => set("confirm_password", e.target.value)} />
                </Field>
              </div>
            </div>
          )}

          {/* STEP 1 */}
          {step === 1 && (
            <div className="space-y-4" data-testid="register-step-data">
              <SectionTitle icon={IdCard} title="Data Diri" subtitle="Sesuai dengan kartu identitas (KTP)" />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="No KTP" required>
                  <input data-testid="reg-ktp" inputMode="numeric" className="inp" value={form.ktp_no} onChange={(e) => set("ktp_no", e.target.value)} />
                </Field>
                <Field label="No Rekening">
                  <input data-testid="reg-account" className="inp" value={form.account_no} onChange={(e) => set("account_no", e.target.value)} />
                </Field>
                <Field label="Tempat Lahir">
                  <input data-testid="reg-pob" className="inp" value={form.place_of_birth} onChange={(e) => set("place_of_birth", e.target.value)} />
                </Field>
                <Field label="Tanggal Lahir">
                  <input data-testid="reg-dob" type="date" className="inp" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} />
                </Field>
                <Field label="Jenis Kelamin">
                  <select data-testid="reg-gender" className="inp" value={form.gender} onChange={(e) => set("gender", e.target.value)}>
                    <option value="L">Laki-laki</option>
                    <option value="P">Perempuan</option>
                  </select>
                </Field>
                <Field label="Pekerjaan">
                  <input data-testid="reg-occupation" className="inp" value={form.occupation} onChange={(e) => set("occupation", e.target.value)} />
                </Field>
              </div>
              <Field label="Alamat">
                <textarea data-testid="reg-address" className="inp h-24" value={form.address} onChange={(e) => set("address", e.target.value)} />
              </Field>
            </div>
          )}

          {/* STEP 2 — Photos */}
          {step === 2 && (
            <div className="space-y-6" data-testid="register-step-foto">
              <SectionTitle icon={Camera} title="Foto Diri & KTP" subtitle="Unggah atau ambil melalui kamera ponsel" />
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <div className="text-sm font-semibold text-brand-ink mb-2">Foto Diri (Selfie)</div>
                  <PhotoCapture testid="photo-selfie" value={form.photo_selfie} onChange={(v) => set("photo_selfie", v)} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-brand-ink mb-2">Foto KTP</div>
                  <PhotoCapture testid="photo-ktp" value={form.photo_ktp} onChange={(v) => set("photo_ktp", v)} />
                </div>
              </div>
            </div>
          )}

          {/* STEP 3 — Signature */}
          {step === 3 && (
            <div className="space-y-4" data-testid="register-step-ttd">
              <SectionTitle icon={PenTool} title="Tanda Tangan Digital" subtitle="Buat tanda tangan langsung di area di bawah" />
              <SignaturePad value={form.signature} onChange={(v) => set("signature", v)} />
              <div className="rounded-2xl bg-brand-soft text-brand text-sm p-4">
                Gunakan jari (layar sentuh) atau mouse untuk membuat tanda tangan. Klik <b>Ulangi</b> untuk menggambar ulang.
              </div>
            </div>
          )}

          {/* STEP 4 — Review */}
          {step === 4 && (
            <div className="space-y-4" data-testid="register-step-review">
              <SectionTitle icon={FileBadge} title="Review Data" subtitle="Pastikan data Anda sudah benar sebelum dikirim" />
              <div className="grid sm:grid-cols-2 gap-3 text-sm">
                <Info label="Nama" value={form.full_name} />
                <Info label="Email" value={form.email} />
                <Info label="No HP" value={form.phone} />
                <Info label="No KTP" value={form.ktp_no} />
                <Info label="No Rekening" value={form.account_no || "-"} />
                <Info label="Tempat / Tgl Lahir" value={`${form.place_of_birth || "-"}${form.date_of_birth ? ", " + form.date_of_birth : ""}`} />
                <Info label="Alamat" value={form.address || "-"} />
              </div>
              <div className="grid grid-cols-3 gap-3 mt-4">
                {form.photo_selfie && <img src={form.photo_selfie} alt="selfie" className="rounded-xl aspect-square object-cover border border-brand/10" />}
                {form.photo_ktp && <img src={form.photo_ktp} alt="ktp" className="rounded-xl aspect-square object-cover border border-brand/10" />}
                {form.signature && <img src={form.signature} alt="ttd" className="rounded-xl aspect-square object-contain bg-white border border-brand/10" />}
              </div>
              <div className="rounded-2xl bg-amber-50 border border-amber-200 text-amber-700 text-sm p-4">
                Setelah pendaftaran dikirim, akun Anda akan berstatus <b>menunggu persetujuan admin</b>.
              </div>
            </div>
          )}

          {error && (
            <div className="mt-5 rounded-2xl bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3" data-testid="register-error">
              {error}
            </div>
          )}

          <div className="mt-6 flex items-center justify-between">
            <button
              type="button"
              onClick={back}
              disabled={step === 0}
              data-testid="register-back-btn"
              className="rounded-full border border-brand/20 text-brand-muted px-5 py-2.5 text-sm flex items-center gap-2 disabled:opacity-40"
            >
              <ArrowLeft size={14} /> Kembali
            </button>
            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={next}
                data-testid="register-next-btn"
                className="rounded-full bg-brand text-white px-6 py-2.5 text-sm flex items-center gap-2 hover:bg-brand-hover transition"
              >
                Lanjut <ArrowRight size={14} />
              </button>
            ) : (
              <LoadingButton
                type="button"
                onClick={submit}
                loading={submitting}
                loadingText="Mengirim…"
                data-testid="register-submit-btn"
                className="rounded-full bg-gold text-brand-ink font-bold px-6 py-2.5 text-sm hover:brightness-95"
              >
                Kirim Pendaftaran <Check size={14} />
              </LoadingButton>
            )}
          </div>
        </div>
      </main>

      <style>{`.inp{width:100%;border-radius:14px;border:1px solid rgba(11,110,110,0.2);padding:10px 14px;font-size:14px;background:white;outline:none}.inp:focus{box-shadow:0 0 0 3px rgba(11,110,110,0.15);border-color:rgba(11,110,110,0.4)}`}</style>
    </div>
  );
}

function SectionTitle({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3 mb-2">
      <div className="w-10 h-10 rounded-xl bg-brand-soft text-brand flex items-center justify-center"><Icon size={18} /></div>
      <div>
        <div className="font-heading font-bold text-brand-ink">{title}</div>
        <div className="text-xs text-brand-muted">{subtitle}</div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="text-sm font-semibold text-brand-ink mb-1.5">{label}{required && <span className="text-red-500"> *</span>}</div>
      {children}
    </label>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-xl bg-brand-soft/60 px-3 py-2">
      <div className="text-[10px] uppercase tracking-[0.2em] text-brand-muted">{label}</div>
      <div className="text-sm font-medium text-brand-ink truncate">{value || "-"}</div>
    </div>
  );
}
